import { t as uiText } from '../../i18n';

export const DEFAULT_APPEARANCE_COLOR = '#cfae91';
const DEFAULT_MAIN_TEXT = '#334155';
const DEFAULT_SUB_TEXT = '#64748b';

export type ButtonPreset =
  | 'right-top'
  | 'right-middle'
  | 'right-bottom'
  | 'left-top'
  | 'left-middle'
  | 'left-bottom'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type WidgetPreset =
  | 'center'
  | 'right-center'
  | 'right-bottom'
  | 'left-center'
  | 'left-bottom';

export interface AppearanceOffset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface AppearanceIcon {
  enabled: boolean;
  url: string;
  size: number;
  color: string;
  colorEnabled: boolean;
  position: 'before' | 'after';
}

export interface AppearanceCloseButton {
  iconColor: string;
  backgroundColor: string;
  opacity: number;
  borderColor: string;
}

export interface AppearanceTooltip {
  enabled: boolean;
  text: string;
  backgroundColor: string;
  textColor: string;
  textAlign: 'center' | 'left';
  maxWidth: number;
}

export interface AppearanceDraft {
  buttonPosition: ButtonPreset;
  buttonOffset: AppearanceOffset;
  edge: 'content' | 'window';
  buttonText: string;
  textColor: string;
  fontSize: number;
  fontWeight: number;
  writingMode: 'horizontal' | 'vertical';
  tcyEnabled: boolean;
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  borderRadius: number;
  buttonWidth: number;
  buttonHeight: number;
  paddingX: number;
  paddingY: number;
  showCloseButton: boolean;
  closeButton: AppearanceCloseButton;
  buttonType: 'text' | 'image';
  imageUrl: string;
  imageFit: 'contain' | 'cover' | 'fill';
  imageMode: 'styled' | 'image-only';
  icon: AppearanceIcon;
  tooltip: AppearanceTooltip;
  widgetPosition: WidgetPreset;
  widgetWidth: number;
  widgetOffset: AppearanceOffset;
  closeOnOutsideClick: boolean;
  dimBackground: boolean;
  mainColor: string;
  linkTextColor: string;
  mainTextColor: string;
  subTextColor: string;
  keywordTextColor: string;
  contentTitle: string;
  contentSubtitle: string;
  contentPlaceholder: string;
}

/** 見た目パネルの保存で上書きしてよい項目。画像・色は STANDARD、文言は FREE から。 */
export interface AppearanceWriteCaps {
  buttonImage: boolean;
  icon: boolean;
  tooltip: boolean;
  designColor: boolean;
  contentText: boolean;
}

export const APPEARANCE_CAPS_FREE: AppearanceWriteCaps = {
  buttonImage: false,
  icon: false,
  tooltip: false,
  designColor: false,
  contentText: true,
};

export const APPEARANCE_CAPS_STANDARD: AppearanceWriteCaps = {
  buttonImage: true,
  icon: true,
  tooltip: true,
  designColor: true,
  contentText: true,
};

const CONTENT_TITLE_MAX = 100;
const CONTENT_SUBTITLE_MAX = 200;
const CONTENT_PLACEHOLDER_MAX = 100;

export interface AppearanceSiteColors {
  mainColor: string;
  linkTextColor: string;
  mainTextColor: string;
  subTextColor: string;
  keywordTextColor: string;
}

export interface AppearanceContentText {
  contentTitle: string;
  contentSubtitle: string;
  contentPlaceholder: string;
}

export type AppearanceSiteWrite = Partial<AppearanceSiteColors> & Partial<AppearanceContentText>;

const BUTTON_PRESETS: ButtonPreset[] = [
  'right-top',
  'right-middle',
  'right-bottom',
  'left-top',
  'left-middle',
  'left-bottom',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

const WIDGET_PRESETS: WidgetPreset[] = [
  'center',
  'right-center',
  'right-bottom',
  'left-center',
  'left-bottom',
];

const ZERO_OFFSET: AppearanceOffset = { top: 0, right: 0, bottom: 0, left: 0 };

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function isHex6(value: string | undefined): value is string {
  return !!value && /^#[0-9a-fA-F]{6}$/.test(value);
}

function hex6(value: string, fallback: string): string {
  return isHex6(value) ? value : fallback;
}

function hexColor(value: string, fallback: string): string {
  return /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(value) ? value : fallback;
}

function asButtonPreset(value: unknown): ButtonPreset {
  return BUTTON_PRESETS.includes(value as ButtonPreset) ? (value as ButtonPreset) : 'right-bottom';
}

function asWidgetPreset(value: unknown): WidgetPreset {
  return WIDGET_PRESETS.includes(value as WidgetPreset) ? (value as WidgetPreset) : 'center';
}

function asImageFit(value: unknown): AppearanceDraft['imageFit'] {
  return value === 'cover' || value === 'fill' || value === 'contain' ? value : 'contain';
}

function readOffset(value: unknown): AppearanceOffset {
  const offset = (value ?? {}) as Record<string, unknown>;
  return {
    top: num(offset.top, 0),
    right: num(offset.right, 0),
    bottom: num(offset.bottom, 0),
    left: num(offset.left, 0),
  };
}

function readIcon(button: Record<string, unknown>): AppearanceIcon {
  const icon = (button.icon ?? {}) as Record<string, unknown>;
  const url = typeof icon.url === 'string' ? icon.url : '';
  return {
    enabled: icon.enabled === true || (icon.enabled !== false && url.length > 0),
    url,
    size: num(icon.size, 20),
    color: typeof icon.color === 'string' && isHex6(icon.color) ? icon.color : '#ffffff',
    colorEnabled: icon.colorEnabled !== false,
    position: icon.position === 'after' ? 'after' : 'before',
  };
}

export function appearanceDraftsEqual(a: AppearanceDraft, b: AppearanceDraft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function defaultAppearanceDraft(): AppearanceDraft {
  return {
    buttonPosition: 'right-bottom',
    buttonOffset: { ...ZERO_OFFSET },
    edge: 'content',
    buttonText: uiText('Frequently asked questions'),
    textColor: '#ffffff',
    fontSize: 16,
    fontWeight: 700,
    writingMode: 'horizontal',
    tcyEnabled: false,
    backgroundColor: DEFAULT_APPEARANCE_COLOR,
    borderColor: '#00000000',
    borderWidth: 0,
    borderRadius: 30,
    buttonWidth: 220,
    buttonHeight: 60,
    paddingX: 8,
    paddingY: 0,
    showCloseButton: false,
    closeButton: {
      iconColor: '#ffffff',
      backgroundColor: '#000000',
      opacity: 0.6,
      borderColor: '#ffffff',
    },
    buttonType: 'text',
    imageUrl: '',
    imageFit: 'contain',
    imageMode: 'styled',
    icon: {
      enabled: false,
      url: '',
      size: 20,
      color: '#ffffff',
      colorEnabled: true,
      position: 'before',
    },
    tooltip: {
      enabled: false,
      text: '',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      textColor: '#ffffff',
      textAlign: 'center',
      maxWidth: 200,
    },
    widgetPosition: 'center',
    widgetWidth: 400,
    widgetOffset: { ...ZERO_OFFSET },
    closeOnOutsideClick: false,
    dimBackground: false,
    mainColor: DEFAULT_APPEARANCE_COLOR,
    linkTextColor: DEFAULT_APPEARANCE_COLOR,
    mainTextColor: DEFAULT_MAIN_TEXT,
    subTextColor: DEFAULT_SUB_TEXT,
    keywordTextColor: DEFAULT_SUB_TEXT,
    contentTitle: '',
    contentSubtitle: '',
    contentPlaceholder: '',
  };
}

export function appearanceDraftFromData(
  widget: Record<string, unknown> | null,
  site: {
    mainColor?: string;
    linkTextColor?: string;
    mainTextColor?: string;
    subTextColor?: string;
    keywordTextColor?: string;
    contentTitle?: string;
    contentSubtitle?: string;
    contentPlaceholder?: string;
  } | null,
): AppearanceDraft {
  const base = defaultAppearanceDraft();
  const button = (widget?.button ?? {}) as Record<string, unknown>;
  const closeButton = (widget?.closeButton ?? {}) as Record<string, unknown>;
  const position = (widget?.position ?? {}) as Record<string, unknown>;
  const tooltip = (widget?.tooltip ?? {}) as Record<string, unknown>;
  const siteMain = isHex6(site?.mainColor) ? site.mainColor : undefined;
  const buttonBackground = isHex6(button.backgroundColor as string | undefined)
    ? (button.backgroundColor as string)
    : siteMain;

  return {
    ...base,
    buttonPosition: asButtonPreset(position.preset),
    buttonOffset: readOffset(position.offset),
    edge: widget?.edge === 'window' ? 'window' : 'content',
    buttonText: typeof button.text === 'string' && button.text ? button.text : base.buttonText,
    textColor: isHex6(button.textColor as string | undefined) ? (button.textColor as string) : base.textColor,
    fontSize: num(button.fontSize, base.fontSize),
    fontWeight: num(button.fontWeight, base.fontWeight),
    writingMode: button.writingMode === 'vertical' ? 'vertical' : 'horizontal',
    tcyEnabled: button.tcyEnabled === true,
    backgroundColor: buttonBackground ?? base.backgroundColor,
    borderColor: typeof button.borderColor === 'string' ? button.borderColor : base.borderColor,
    borderWidth: num(button.borderWidth, base.borderWidth),
    borderRadius: num(button.borderRadius, base.borderRadius),
    buttonWidth: num(button.width, base.buttonWidth),
    buttonHeight: num(button.height, base.buttonHeight),
    paddingX: num(button.paddingX, base.paddingX),
    paddingY: num(button.paddingY, base.paddingY),
    showCloseButton: button.showCloseButton === true,
    closeButton: {
      iconColor: isHex6(closeButton.iconColor as string | undefined)
        ? (closeButton.iconColor as string)
        : base.closeButton.iconColor,
      backgroundColor: isHex6(closeButton.backgroundColor as string | undefined)
        ? (closeButton.backgroundColor as string)
        : base.closeButton.backgroundColor,
      opacity: num(closeButton.opacity, base.closeButton.opacity),
      borderColor: isHex6(closeButton.borderColor as string | undefined)
        ? (closeButton.borderColor as string)
        : base.closeButton.borderColor,
    },
    buttonType: button.buttonType === 'image' ? 'image' : 'text',
    imageUrl: typeof button.imageUrl === 'string' ? button.imageUrl : '',
    imageFit: asImageFit(button.imageFit),
    imageMode: button.imageMode === 'image-only' ? 'image-only' : 'styled',
    icon: readIcon(button),
    tooltip: {
      enabled: tooltip.enabled === true,
      text: typeof tooltip.text === 'string' ? tooltip.text : '',
      backgroundColor:
        typeof tooltip.backgroundColor === 'string' && tooltip.backgroundColor
          ? tooltip.backgroundColor
          : base.tooltip.backgroundColor,
      textColor: isHex6(tooltip.textColor as string | undefined)
        ? (tooltip.textColor as string)
        : base.tooltip.textColor,
      textAlign: tooltip.textAlign === 'left' ? 'left' : 'center',
      maxWidth: num(tooltip.maxWidth, base.tooltip.maxWidth),
    },
    widgetPosition: asWidgetPreset(widget?.widgetPosition),
    widgetWidth: num(widget?.widgetWidth, base.widgetWidth),
    widgetOffset: readOffset(widget?.widgetOffset),
    closeOnOutsideClick: widget?.closeOnOutsideClick === true,
    dimBackground: widget?.dimBackground === true,
    mainColor: siteMain ?? base.mainColor,
    linkTextColor: isHex6(site?.linkTextColor) ? site.linkTextColor : (siteMain ?? base.linkTextColor),
    mainTextColor: isHex6(site?.mainTextColor) ? site.mainTextColor : base.mainTextColor,
    subTextColor: isHex6(site?.subTextColor) ? site.subTextColor : base.subTextColor,
    keywordTextColor: isHex6(site?.keywordTextColor) ? site.keywordTextColor : base.keywordTextColor,
    contentTitle: clipText(site?.contentTitle, CONTENT_TITLE_MAX),
    contentSubtitle: clipText(site?.contentSubtitle, CONTENT_SUBTITLE_MAX),
    contentPlaceholder: clipText(site?.contentPlaceholder, CONTENT_PLACEHOLDER_MAX),
  };
}

function clipText(value: string | undefined, max: number): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

/**
 * 既存ドキュメントを残したまま、このパネルが権限を持つキーだけ上書きする。
 * updatedAt は呼び出し側で付ける。
 */
export function buildAppearanceWrites(
  prev: Record<string, unknown>,
  draft: AppearanceDraft,
  caps: AppearanceWriteCaps,
): { widget: Record<string, unknown>; site: AppearanceSiteWrite | null } {
  const prevButton = (prev.button ?? {}) as Record<string, unknown>;
  const button: Record<string, unknown> = {
    ...prevButton,
    text: draft.buttonText,
    textColor: hex6(draft.textColor, '#ffffff'),
    fontSize: num(draft.fontSize, 16),
    fontWeight: num(draft.fontWeight, 700),
    writingMode: draft.writingMode === 'vertical' ? 'vertical' : 'horizontal',
    tcyEnabled: draft.tcyEnabled === true,
    backgroundColor: hex6(draft.backgroundColor, DEFAULT_APPEARANCE_COLOR),
    borderColor: hexColor(draft.borderColor, '#00000000'),
    borderWidth: num(draft.borderWidth, 0),
    borderRadius: num(draft.borderRadius, 30),
    width: num(draft.buttonWidth, 220),
    height: num(draft.buttonHeight, 60),
    paddingX: num(draft.paddingX, 8),
    paddingY: num(draft.paddingY, 0),
    showCloseButton: draft.showCloseButton === true,
  };

  if (caps.buttonImage) {
    button.buttonType = draft.buttonType === 'image' ? 'image' : 'text';
    button.imageUrl = draft.imageUrl;
    button.imageFit = asImageFit(draft.imageFit);
    button.imageMode = draft.imageMode === 'image-only' ? 'image-only' : 'styled';
  }

  if (caps.icon) {
    button.icon = {
      enabled: draft.icon.enabled === true,
      url: draft.icon.url,
      size: num(draft.icon.size, 20),
      color: hex6(draft.icon.color, '#ffffff'),
      colorEnabled: draft.icon.colorEnabled !== false,
      position: draft.icon.position === 'after' ? 'after' : 'before',
    };
  }

  const widget: Record<string, unknown> = {
    enabled: true,
    theme: 'light',
    locale: 'ja',
    cacheMode: 'cached',
    ...prev,
    appearanceSaved: true,
    edge: draft.edge === 'window' ? 'window' : 'content',
    closeOnOutsideClick: draft.closeOnOutsideClick === true,
    dimBackground: draft.dimBackground === true,
    button,
    position: {
      ...((prev.position ?? {}) as Record<string, unknown>),
      preset: asButtonPreset(draft.buttonPosition),
      offset: { ...draft.buttonOffset },
    },
    widgetPosition: asWidgetPreset(draft.widgetPosition),
    widgetOffset: { ...draft.widgetOffset },
    widgetWidth: num(draft.widgetWidth, 400),
    closeButton: {
      iconColor: hex6(draft.closeButton.iconColor, '#ffffff'),
      backgroundColor: hex6(draft.closeButton.backgroundColor, '#000000'),
      opacity: num(draft.closeButton.opacity, 0.6),
      borderColor: hex6(draft.closeButton.borderColor, '#ffffff'),
    },
  };

  if (caps.tooltip) {
    const prevTooltip = (prev.tooltip ?? {}) as Record<string, unknown>;
    widget.tooltip = {
      ...prevTooltip,
      enabled: draft.tooltip.enabled === true,
      text: draft.tooltip.text,
      backgroundColor: draft.tooltip.backgroundColor || 'rgba(0, 0, 0, 0.8)',
      textColor: hex6(draft.tooltip.textColor, '#ffffff'),
      textAlign: draft.tooltip.textAlign === 'left' ? 'left' : 'center',
      maxWidth: num(draft.tooltip.maxWidth, 200),
    };
  }

  const site: AppearanceSiteWrite = {};
  if (caps.designColor) {
    site.mainColor = hex6(draft.mainColor, DEFAULT_APPEARANCE_COLOR);
    site.linkTextColor = hex6(draft.linkTextColor, DEFAULT_APPEARANCE_COLOR);
    site.mainTextColor = hex6(draft.mainTextColor, DEFAULT_MAIN_TEXT);
    site.subTextColor = hex6(draft.subTextColor, DEFAULT_SUB_TEXT);
    site.keywordTextColor = hex6(draft.keywordTextColor, DEFAULT_SUB_TEXT);
  }
  if (caps.contentText) {
    site.contentTitle = clipText(draft.contentTitle, CONTENT_TITLE_MAX);
    site.contentSubtitle = clipText(draft.contentSubtitle, CONTENT_SUBTITLE_MAX);
    site.contentPlaceholder = clipText(draft.contentPlaceholder, CONTENT_PLACEHOLDER_MAX);
  }

  return { widget, site: Object.keys(site).length > 0 ? site : null };
}

export const BUTTON_POSITION_OPTIONS: { value: ButtonPreset; label: string }[] = [
  { value: 'right-top', get label() { return uiText('Right side of the screen, top'); } },
  { value: 'right-middle', get label() { return uiText('Right side of the screen, center'); } },
  { value: 'right-bottom', get label() { return uiText('Right side of the screen, bottom'); } },
  { value: 'left-top', get label() { return uiText('Left side of the screen, top'); } },
  { value: 'left-middle', get label() { return uiText('Left side of the screen, center'); } },
  { value: 'left-bottom', get label() { return uiText('Left side of the screen, bottom'); } },
  { value: 'bottom-left', get label() { return uiText('Bottom of the screen, left'); } },
  { value: 'bottom-center', get label() { return uiText('Bottom of the screen, center'); } },
  { value: 'bottom-right', get label() { return uiText('Bottom of the screen, right'); } },
];

export const WIDGET_POSITION_OPTIONS: { value: WidgetPreset; label: string }[] = [
  { value: 'center', get label() { return uiText('Center of the screen'); } },
  { value: 'right-center', get label() { return uiText('Center right of the screen'); } },
  { value: 'right-bottom', get label() { return uiText('Bottom right of the screen'); } },
  { value: 'left-center', get label() { return uiText('Center left of the screen'); } },
  { value: 'left-bottom', get label() { return uiText('Bottom left of the screen'); } },
];
